/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components */
import { tracked } from '@glimmer/tracking';
import {
  attributeBindings,
  classNameBindings,
  classNames,
} from '@ember-decorators/component';
import Component from '@ember/component';
import { set, action, computed } from '@ember/object';
import { htmlSafe } from '@ember/template';
import { isColour } from '@linc-technologies/linced-common/utils/colour';

// How long the pointer must rest on a slider position before its coachmark
// appears, so it doesn't pop up while the teacher is just moving across.
const TOOLTIP_DELAY_MS = 1000;

@classNames('reporting-progress-slider')
@classNameBindings(
  'disabled:reporting-progress-slider--disabled',
  'showPreviousState:reporting-progress-slider--previous',
)
@attributeBindings('tabindex')
export default class ProgressSlider extends Component {
  tabindex = '0';
  trackH = 16;
  hoverTrackH = 22;
  thumbSize = 24;
  hoverThumbSize = 30;
  containerH = 30;
  dotSize = 8;

  // passed in
  levels = null; // [{value, string, colour}]

  @tracked value = null;
  onChange = null;
  label = null;
  compact = false; // true → only show active label under thumb
  disabled = false;
  @tracked tooltips = null; // [{label, items: [string]}] one per level
  onViewRubric = null;
  color = null; // optional solid fill override (skips per-level colour)
  reverseColors = false; // reverse the GRADE_COLOURS lookup by position (e.g. effort)
  @tracked isNA = false;
  onSetNA = null;
  onReEnable = null;
  @tracked isPrevious = false;
  // Where a pre-filled value came from, e.g. "Year 4 mid-year, 14 Jun 2025"
  previousSource = null;

  // internal
  @tracked currentValue = null;

  @tracked isHovered = false;
  @tracked tooltipIndex = null;
  isTooltipHovered = false;
  @tracked isTooltipDismissed = false;
  @tracked tooltipRect = null;

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    set(this, 'currentValue', this.value);
  }

  @computed('currentValue', 'levels.[]')
  get activeIndex() {
    let val = this.currentValue;
    let levels = this.levels;
    if (!levels || !levels.length || val == null) {
      return -1;
    }
    return levels.findIndex((l) => l.value === val);
  }

  @computed('activeIndex')
  get isUnset() {
    return this.activeIndex < 0;
  }

  // Apply the "previous entry" treatment only while the value is genuinely
  // pre-filled from history. Once the teacher moves the slider currentValue
  // diverges from the parent value until the save round-trips — drop the
  // visual cue immediately so the user sees their input as committed.
  @computed('isPrevious', 'isUnset', 'isNA', 'currentValue', 'value')
  get showPreviousState() {
    return (
      this.isPrevious &&
      !this.isUnset &&
      !this.isNA &&
      this.currentValue === this.value
    );
  }

  @computed('activeIndex', 'levels.[]')
  get position() {
    let idx = this.activeIndex;
    let count = (this.levels || []).length;
    if (idx < 0 || count <= 1) {
      return 0;
    }
    return idx / (count - 1);
  }

  @computed('currentValue', 'levels.[]', 'showPreviousState')
  get fillColor() {
    let val = this.currentValue;
    if (val == null) {
      return '#D1D5DB';
    }
    // Pre-filled-from-history value — render fill + thumb in neutral grey
    // so it visibly reads as "not yet committed for this report" instead
    // of competing with sliders the teacher has actually saved.
    if (this.showPreviousState) {
      return '#9CA3AF';
    }
    let level = (this.levels || []).find((l) => l.value === val);
    if (!level) {
      return '#D1D5DB';
    }
    return isColour(level.colour) ? level.colour : '#26a69a';
  }

  @computed('currentValue', 'levels.[]')
  get currentLabel() {
    let val = this.currentValue;
    if (val == null) {
      return null;
    }
    let level = (this.levels || []).find((l) => l.value === val);
    return level ? level.string : null;
  }

  get trackBgStyle() {
    let th = this.isHovered ? this.hoverTrackH : this.trackH;
    let top = (this.containerH - th) / 2;
    return htmlSafe(
      `height: ${th}px; top: ${top}px; left: 0; right: 0;` +
        ` position: absolute; background: #FFFFFF; border-radius: ${
          th / 2
        }px;` +
        ` box-shadow: inset 0 1px 3px rgba(0,0,0,0.08);` +
        ` transition: height 0.15s ease, top 0.15s ease;`,
    );
  }

  @computed('position', 'fillColor', 'isUnset', 'isHovered')
  get fillStyle() {
    if (this.isUnset) {
      return htmlSafe('display: none;');
    }
    let pos = this.position;
    let th = this.isHovered ? this.hoverTrackH : this.trackH;
    let top = (this.containerH - th) / 2;
    return htmlSafe(
      `width: calc(${th / 2}px + (100% - ${th}px) * ${pos});` +
        ` height: ${th}px; top: ${top}px; left: 0; position: absolute;` +
        ` background: ${this.fillColor}; border-radius: ${th / 2}px;` +
        ` transition: width 0.2s ease-out, background 0.25s ease, height 0.15s ease, top 0.15s ease;`,
    );
  }

  @computed('position', 'fillColor', 'isUnset', 'isHovered')
  get thumbStyle() {
    if (this.isUnset) {
      return htmlSafe('display: none;');
    }
    let pos = this.position;
    let size = this.isHovered ? this.hoverThumbSize : this.thumbSize;
    let th = this.isHovered ? this.hoverTrackH : this.trackH;
    return htmlSafe(
      `width: ${size}px; height: ${size}px;` +
        ` left: calc(${th / 2}px + (100% - ${th}px) * ${pos});` +
        ` top: 50%; position: absolute; transform: translate(-50%, -50%);` +
        ` background: ${this.fillColor}; border: 3px solid #FFFFFF; border-radius: 50%;` +
        ` box-shadow: 0 1px 4px rgba(0,0,0,0.25); cursor: grab; z-index: 10;` +
        ` transition: width 0.15s ease, height 0.15s ease, left 0.2s ease-out, background 0.25s ease;`,
    );
  }

  @computed('levels.[]', 'activeIndex', 'isHovered')
  get snapDots() {
    let levels = this.levels || [];
    let activeIdx = this.activeIndex;
    let count = levels.length;
    let th = this.isHovered ? this.hoverTrackH : this.trackH;
    return levels.map((level, i) => {
      let pct = count > 1 ? i / (count - 1) : 0.5;
      let filled = activeIdx >= 0 && i <= activeIdx;
      let bg = filled ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.12)';
      return {
        level,
        dotStyle: htmlSafe(
          `width: ${this.dotSize}px; height: ${this.dotSize}px;` +
            ` top: 50%; left: calc(${th / 2}px + (100% - ${th}px) * ${pct});` +
            ` transform: translate(-50%, -50%); position: absolute; border-radius: 50%;` +
            ` background: ${bg}; z-index: 1;` +
            ` transition: background 0.25s ease, left 0.2s ease;`,
        ),
      };
    });
  }

  @computed('position', 'fillColor', 'isUnset')
  get compactLabelStyle() {
    if (this.isUnset) {
      return htmlSafe(
        'left: 50%; transform: translateX(-50%); position: absolute; color: #9CA3AF;',
      );
    }
    let pos = this.position;
    let th = this.trackH;
    let transform =
      pos === 0
        ? 'translateX(0)'
        : pos === 1
          ? 'translateX(-100%)'
          : 'translateX(-50%)';
    return htmlSafe(
      `left: calc(${th / 2}px + (100% - ${th}px) * ${pos});` +
        ` transform: ${transform}; position: absolute; color: ${this.fillColor};` +
        ` transition: left 0.2s ease-out, color 0.25s ease;`,
    );
  }

  @computed('tooltipIndex', 'tooltips', 'levels.[]')
  get tooltipData() {
    let idx = this.tooltipIndex;
    if (idx == null) {
      return null;
    }
    let tooltips = this.tooltips;
    if (!tooltips || !tooltips.length) {
      return null;
    }
    let tooltip = tooltips[idx];
    if (!tooltip || !tooltip.items || !tooltip.items.length) {
      return null;
    }
    let level = (this.levels || [])[idx];
    let allItems = tooltip.items;
    let isTruncated = allItems.length > 2;
    let levelColour = isColour(level?.colour) ? level.colour : '#26a69a';
    return {
      label: level ? level.string : tooltip.label,
      colour: levelColour,
      items: isTruncated ? allItems.slice(0, 2) : allItems,
      isTruncated,
    };
  }

  @computed('tooltipIndex', 'levels.[]', 'tooltipRect')
  get tooltipWrapperStyle() {
    let idx = this.tooltipIndex;
    if (idx == null) {
      return htmlSafe('display: none;');
    }
    let levels = this.levels || [];
    let count = levels.length;
    let th = this.trackH;
    let pct = count > 1 ? idx / (count - 1) : 0.5;
    let isFirst = idx === 0;
    let isLast = idx === count - 1;
    let rect = this.tooltipRect;
    if (rect) {
      let left = rect.left + th / 2 + (rect.width - th) * pct;
      let transformX = isFirst ? '0' : isLast ? '-100%' : '-50%';
      return htmlSafe(
        `position: fixed; top: ${rect.top}px; padding-bottom: 6px;` +
          ` left: ${left}px;` +
          ` transform: translate(${transformX}, -100%); z-index: 1000;`,
      );
    }
    let transform = isFirst
      ? 'translate(0, -100%)'
      : isLast
        ? 'translate(-100%, -100%)'
        : 'translate(-50%, -100%)';
    return htmlSafe(
      `position: absolute; top: 0; padding-bottom: 6px;` +
        ` left: calc(${th / 2}px + (100% - ${th}px) * ${pct});` +
        ` transform: ${transform}; z-index: 20;` +
        ` transition: left 0.2s ease-out;`,
    );
  }

  @computed('tooltipData.colour')
  get tooltipBadgeStyle() {
    let colour = this.get('tooltipData.colour');
    if (!colour) {
      return htmlSafe('');
    }
    return htmlSafe(`background-color: ${colour};`);
  }

  @computed('tooltipData.colour')
  get tooltipSeeMoreStyle() {
    let colour = this.get('tooltipData.colour');
    if (!colour) {
      return htmlSafe('');
    }
    return htmlSafe(`color: ${colour};`);
  }

  @computed('tooltipData')
  get tooltipBorderStyle() {
    let data = this.tooltipData;
    if (!data) {
      return htmlSafe('');
    }
    let c = data.colour;
    return htmlSafe(
      `background: linear-gradient(135deg, ${c}80, ${c}, ${c}80);`,
    );
  }

  @computed('levels.[]')
  get fullLabelWrapperStyle() {
    let count = (this.levels || []).length;
    return htmlSafe(`grid-template-columns: repeat(${count}, 1fr);`);
  }

  @computed('levels.[]', 'activeIndex')
  get fullLabels() {
    return (this.levels || []).map((level, i) => ({
      level,
      isActive: i === this.activeIndex,
    }));
  }

  updateTooltip(clientX) {
    if (
      this.disabled ||
      !this.tooltips ||
      !this.tooltips.length ||
      this.isTooltipDismissed
    ) {
      return;
    }
    let trackEl = this.element && this.element.querySelector('.rps__track');
    if (!trackEl) {
      return;
    }
    let rect = trackEl.getBoundingClientRect();
    let levels = this.levels || [];
    let count = levels.length;
    if (!count) {
      return;
    }
    let innerWidth = rect.width - this.trackH;
    let x = clientX - rect.left - this.trackH / 2;
    let pct = Math.max(0, Math.min(x / innerWidth, 1));
    let idx = Math.round(pct * (count - 1));

    // Already showing, or waiting to show, this position's coachmark
    if (idx === this._pendingTooltipIndex) {
      return;
    }

    // Moved to a new position: hide any coachmark and restart the delay
    this._pendingTooltipIndex = idx;
    window.clearTimeout(this.tooltipTimeout);
    set(this, 'tooltipIndex', null);
    this.tooltipTimeout = setTimeout(() => {
      if (this.isDestroying || this.isDestroyed) {
        return;
      }
      // Re-read the track position in case the page scrolled meanwhile
      set(this, 'tooltipIndex', idx);
      set(this, 'tooltipRect', trackEl.getBoundingClientRect());
    }, TOOLTIP_DELAY_MS);
  }

  clearTooltip() {
    window.clearTimeout(this.tooltipTimeout);
    this._pendingTooltipIndex = null;
    set(this, 'tooltipIndex', null);
  }

  pickLevel(clientX) {
    if (this.disabled) {
      return;
    }
    if (!this.element) {
      return;
    }
    let trackEl = this.element.querySelector('.rps__track');
    if (!trackEl) {
      return;
    }
    let rect = trackEl.getBoundingClientRect();
    let th = this.isHovered ? this.hoverTrackH : this.trackH;
    let innerWidth = rect.width - th;
    let x = Math.max(0, Math.min(clientX - rect.left - th / 2, innerWidth));
    let pct = innerWidth > 0 ? x / innerWidth : 0;
    let levels = this.levels || [];
    if (!levels.length) {
      return;
    }
    let idx = Math.round(pct * (levels.length - 1));
    this.applyValue(levels[idx].value);
  }

  applyValue(newValue) {
    set(this, 'currentValue', newValue);
    if (this.onChange) {
      this.onChange(newValue);
    }
  }

  beginDrag(e) {
    if (this.disabled) {
      return;
    }
    e.preventDefault();
    let move = (ev) => {
      let cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      this.pickLevel(cx);
    };
    let end = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', end);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', end);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', end);
    let cx = e.touches ? e.touches[0].clientX : e.clientX;
    this.pickLevel(cx);
  }

  onKeyDown(e) {
    if (this.disabled) {
      return;
    }
    let levels = this.levels || [];
    let idx = this.activeIndex;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      let newIdx = Math.min(idx < 0 ? 0 : idx + 1, levels.length - 1);
      if (levels[newIdx]) {
        this.applyValue(levels[newIdx].value);
      }
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      let newIdx = Math.max(idx < 0 ? 0 : idx - 1, 0);
      if (levels[newIdx]) {
        this.applyValue(levels[newIdx].value);
      }
    }
  }

  @action
  selectLevel(value) {
    this.applyValue(value);
  }

  @action
  setNA(e) {
    e.stopPropagation();
    if (this.onSetNA) this.onSetNA();
  }

  @action
  reEnable(e) {
    e.stopPropagation();
    if (this.onReEnable) this.onReEnable();
  }

  @action
  coachmarkMouseDown(e) {
    e.stopPropagation();
  }

  @action
  tooltipMouseEnter() {
    window.clearTimeout(this.mouseleaveTimeout);
    set(this, 'isTooltipHovered', true);
  }

  @action
  tooltipMouseLeave() {
    set(this, 'isTooltipHovered', false);
    this.clearTooltip();
  }

  @action
  viewRubric(e) {
    e.stopPropagation();
    this.clearTooltip();
    if (this.onViewRubric) {
      this.onViewRubric();
    }
  }

  @action
  dismissTooltip(e) {
    e.stopPropagation();
    this.clearTooltip();
    set(this, 'isTooltipDismissed', true);
  }

  didInsertElement() {
    super.didInsertElement(...arguments);
    let track = this.element.querySelector('.rps__track');
    this.mousedownHandler = (e) => this.beginDrag(e);
    this.mouseenterHandler = () => set(this, 'isHovered', true);
    this.mouseleaveHandler = () => {
      this.isHovered = false;
      this.isTooltipDismissed = false;
      // A coachmark still waiting on its delay never appears once the
      // pointer has left the track
      if (this.tooltipIndex == null) {
        this.clearTooltip();
        return;
      }
      // delay clear so hovering into the coachmark keeps it visible
      this.mouseleaveTimeout = setTimeout(() => {
        if (!this.isTooltipHovered) {
          this.clearTooltip();
        }
      }, 50);
    };
    this.mousemoveHandler = (e) => {
      // The coachmark sits inside the track; moving over it shouldn't pick a
      // new slider position and restart the delay
      if (e.target.closest && e.target.closest('.rps__coachmark-wrapper')) {
        return;
      }
      this.updateTooltip(e.clientX);
    };
    this.keydownHandler = this.onKeyDown.bind(this);
    if (track) {
      track.addEventListener('mousedown', this.mousedownHandler);
      track.addEventListener('touchstart', this.mousedownHandler, {
        passive: false,
      });
      track.addEventListener('mouseenter', this.mouseenterHandler);
      track.addEventListener('mouseleave', this.mouseleaveHandler);
      track.addEventListener('mousemove', this.mousemoveHandler);
    }
    this.element.addEventListener('keydown', this.keydownHandler);
  }

  willDestroyElement() {
    super.willDestroyElement(...arguments);
    let track = this.element.querySelector('.rps__track');
    if (track) {
      track.removeEventListener('mousedown', this.mousedownHandler);
      track.removeEventListener('touchstart', this.mousedownHandler);
      track.removeEventListener('mouseenter', this.mouseenterHandler);
      track.removeEventListener('mouseleave', this.mouseleaveHandler);
      track.removeEventListener('mousemove', this.mousemoveHandler);
    }
    this.element.removeEventListener('keydown', this.keydownHandler);
    window.clearTimeout(this.mouseleaveTimeout);
    window.clearTimeout(this.tooltipTimeout);
  }
}
